import axios from 'axios';

/**
 * Scoped permissions: who may do what, where, until when (ADR-0034).
 *
 * Every call carries the signed-in user's token; the service decides from its
 * own data, never from anything this file sends about who the caller is.
 */
const getEnvVar = (key, fallback) => {
  try {
    return (typeof process !== 'undefined' && process.env && process.env[key]) || fallback;
  } catch (error) {
    return fallback;
  }
};

const BASE_URL = `${getEnvVar('REACT_APP_TAG_SERVICE_URL', 'http://localhost:8082')}/api/v1/tag-management/permissions`;

const authHeaders = () => {
  const token = localStorage.getItem('token') || localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const getScopeTypes = async () => {
  const response = await axios.get(`${BASE_URL}/scope-types`, { headers: authHeaders() });
  return response.data?.scopeTypes || [];
};

export const getGrants = async (subjectType, subjectId) => {
  const response = await axios.get(`${BASE_URL}/grants`, {
    headers: authHeaders(),
    params: { subjectType, subjectId },
  });
  return response.data?.grants || [];
};

export const getEffectivePermissions = async (actorId) => {
  const response = await axios.get(`${BASE_URL}/actors/${actorId}/effective`, { headers: authHeaders() });
  return response.data?.permissions || [];
};

export const createGrant = async (grant) => {
  const response = await axios.post(`${BASE_URL}/grants`, grant, { headers: authHeaders() });
  return response.data;
};

export const revokeGrant = async (grantId) => {
  const response = await axios.delete(`${BASE_URL}/grants/${grantId}`, { headers: authHeaders() });
  return response.data;
};

export const askForCapability = async (capability, reason, requestedScope) => {
  const response = await axios.post(
    `${BASE_URL}/requests`,
    { capability, reason: reason || null, requestedScope: requestedScope || null },
    { headers: authHeaders() },
  );
  return response.data;
};

export const getAccessRequests = async (status = 'pending') => {
  const response = await axios.get(`${BASE_URL}/requests`, {
    headers: authHeaders(),
    params: { status },
  });
  return response.data?.requests || [];
};

export const approveAccessRequest = async (requestId, { scopes = [], validUntil = null, note = null } = {}) => {
  const response = await axios.post(
    `${BASE_URL}/requests/${requestId}/approve`,
    { scopes, validUntil, note },
    { headers: authHeaders() },
  );
  return response.data;
};

export const declineAccessRequest = async (requestId, note) => {
  const response = await axios.post(
    `${BASE_URL}/requests/${requestId}/decline`,
    { note: note || null },
    { headers: authHeaders() },
  );
  return response.data;
};

export const withdrawAccessRequest = async (requestId) => {
  const response = await axios.delete(`${BASE_URL}/requests/${requestId}`, { headers: authHeaders() });
  return response.data;
};

export default {
  getScopeTypes,
  getGrants,
  getEffectivePermissions,
  createGrant,
  revokeGrant,
  askForCapability,
  getAccessRequests,
  approveAccessRequest,
  declineAccessRequest,
  withdrawAccessRequest,
};
